(function(angular, _) {
    /**
     * @ngdoc directive
     * @name DropdownAccessibilityController
     * @description Directive used to enhance the UI bootstrap dropdown with accessibility functionalities
     * - Makes dropdown toggle element keyboard navigable (Tab | Shift+Tab)
     * - Opens the dropdown on Enter when the dropdown-toggle is focused
     * - Focuses the active menu item on dropdown open
     * - Focuses the menu item that is currently mouse hovered
     * - Focus dropdown list item that matches the user typing (dropdown menu lookup based on keypresses)
     * Designed to be used on the same element as the UI bootstrap dropdown directive
     *
     * <div dropdown dropdown-accessibility>
     *     <a dropdown-toggle>Toggle</a>
     *     ...
     * </div>
     */
    class DropdownAccessibilityController {
        static get $inject() {
            return ['$element', '$scope', '$document', '$timeout'];
        }

        constructor($element, $scope, $document, $timeout) {
            this.$element = $element;
            this.$scope = $scope;
            this.$document = $document;
            this.$timeout = $timeout;

            this.watchers = {};

            this.dropdownToggleWatcherCallback = this.dropdownToggleWatcher.bind(this);
            this.onKeypressCallback = this.onKeypress.bind(this);
            this.onMouseEnterCallback = this.onMouseEnter.bind(this);
            this.onMouseMoveCallback = this.onMouseMove.bind(this);
            this.debouncedOnMouseEnterCallback = _.debounce(this.onMouseEnterCallback, this.debounceWait);
            this.assertFocusCallback = this.assertFocus.bind(this);

            /**
             * The dropdown menu jQuery element holding the list of menu items
             * @type {null|jqLight}
             */
            this.$dropdownMenu = null;

            /**
             * The dropdown menu toggle jQuery element
             * @type {null|jqLight}
             */
            this.$dropdownToggle = null;

            /**
             * Tracks the state of active listening on user input
             * @type {boolean}
             */
            this.listenersInstalled = false;

            /**
             * The minimum length of characters typed by the user before evaluating the menu item to focus
             * @type {number}
             */
            this.minLength = 1;

            /**
             * The timeout in milliseconds before evaluating the item to focus
             * @type {number}
             */
            this.debounceWait = 400;

            /**
             * Keeps the user input to evaluate which menu item to focus
             * @type {string}
             */
            this.searchContent = '';

            /**
             * The list of menu item nodes
             * @type {Array}
             */
            this.menuNodes = [];

            /**
             * State used to indicate if the currently hovered li item should be set as focused
             * The hovered item should be focused only on explicit mouse move. If the hover happened
             * due to underlying menu scroll, do not use the hover focus mechanism (eg. hover because menu scrolled due to scrollIntoView triggered by user typing).
             * @type {boolean}
             */
            this.useMouseFocus = false;

            /**
             * Map of currently running timeouts
             * @type {Object}
             */
            this.timeouts = {};

            /**
             * Keeps track of the dropdown opened state
             * @type {boolean}
             */
            this.isDropdownOpened = false;
        }

        get ENTER_KEY_CODE() {
            return 13;
        }

        get SPACE_KEY_CODE() {
            return 32;
        }

        get UP_KEY_CODE() {
            return 38;
        }

        get DOWN_KEY_CODE() {
            return 40;
        }

        get DROPDOWN_MENU_CLASS() {
            return 'dropdown-menu';
        }

        get DROPDOWN_MENU_SELECTOR() {
            return `.${this.DROPDOWN_MENU_CLASS}`;
        }

        get DROPDOWN_TOGGLE_SELECTOR() {
            return '[dropdown-toggle]';
        }

        get ACTIVE_MENU_ITEM_CLASS() {
            return 'selected';
        }

        $onInit() {
            // Implement the logic of identifying if the dropdown is opened for non angular UI dropdowns (if required)
            if (this.dropdown) {
                this.watchers.isOpen = this.$scope.$watch(this.dropdown.isOpen, (isOpen) => {
                    if (isOpen) {
                        if (this.dropdown.dropdownMenuTemplateUrl) {
                            this.watchers.dropdownMenuReplace = this.$scope.$watch(() => this.dropdown.dropdownMenu, (newMenu, oldMenu) => {
                                const menuTemplateCompiled = (newMenu !== oldMenu);

                                if (menuTemplateCompiled) {
                                    this.watchers.dropdownMenuReplace();
                                    delete this.watchers.dropdownMenuReplace;

                                    this.initDropdownElements();

                                    // Init dropdown menu items asynchronously to allow any inner directive to initialize
                                    this.timeouts.asyncMenuOpen = this.$timeout(() => {
                                        this.onDropdownOpen();
                                    });
                                }
                            });
                        } else {
                            this.onDropdownOpen();
                        }
                    } else {
                        this.onDropdownClose();
                    }
                });
            }
        }

        $postLink() {
            this.initDropdownElements();

            this.$dropdownMenu.attr('role', 'menu');
            this.$dropdownMenu.attr('aria-expanded', false);
            this.$dropdownToggle.attr('tabindex', 0);
            this.$dropdownToggle.attr('data-toggle', 'dropdown');

            this.$dropdownToggle.on('keydown', this.dropdownToggleWatcherCallback);
        }

        $onDestroy() {
            _.forEach(this.watchers, (watcher) => watcher());
            _.forEach(this.timeouts, (timeout) => this.$timeout.cancel(timeout));

            this.$dropdownToggle.off('keydown', this.dropdownToggleWatcherCallback);

            this.onDropdownClose();
        }

        /**
         * @ngdoc method
         * @name DropdownAccessibilityController#dropdownToggleWatcher
         * @description Triggers the dropdown on specific keydowns when the dropdown toggle is focused
         * @param {Event} $event The keydown event
         */
        dropdownToggleWatcher($event) {
            const which = this.getEventKeyCode($event);
            const eventQualifies = _.includes([this.ENTER_KEY_CODE], which);

            if (!this.isDropdownOpened && eventQualifies) {
                $event.preventDefault();
                $event.stopPropagation();
                this.$dropdownToggle.trigger('click');
            }
        }

        /**
         * @ngdoc method
         * @name DropdownAccessibilityController#initDropdownElements
         * @description Finds and stores the dropdown element nodes
         * @returns {DropdownAccessibilityController}
         */
        initDropdownElements() {
            this.$dropdownMenu = this.$element.hasClass(this.DROPDOWN_MENU_CLASS)
                ? this.$element
                : this.$element.find(this.DROPDOWN_MENU_SELECTOR);

            this.$dropdownToggle = this.$element.find(this.DROPDOWN_TOGGLE_SELECTOR);
            return this;
        }

        /**
         * @ngdoc method
         * @name DropdownAccessibilityController#scrollIntoView
         * @description Scrolls the provided menu element item into view if the dropdown menu is scrollable
         * @param {jqLite} $menuListItem The menu list item jqLite node
         */
        scrollIntoView($menuListItem) {
            if (!_.size(this.$dropdownMenu) || !_.size($menuListItem)) {
                return;
            }

            const listItemTop = $menuListItem.position().top;
            const dropdownMenuHeight = this.$dropdownMenu.height();
            const dropdownMenuScrollTop = this.$dropdownMenu.scrollTop();
            const scrollTop = $menuListItem.offset().top - this.$dropdownMenu.offset().top + this.$dropdownMenu.scrollTop();

            const scrollDiff = listItemTop - dropdownMenuScrollTop;

            // Scrolls only in case the element is not visible
            if (scrollDiff < 0 || scrollDiff > dropdownMenuHeight) {
                this.$dropdownMenu.scrollTop(scrollTop);
            }
        }

        /**
         * @ngdoc method
         * @name DropdownAccessibilityController#getDropdownMenuNodes
         * @description Retrieves an array of DOMNodes representing the menu list items
         * @param {boolean=} actionable Include only menu nodes that can be acted upon (containing hyperlink elements).
         * Note that dropdown menu can contain delimiter|header and other non-actionable li elements.
         * @returns {Array}
         */
        getDropdownMenuNodes(actionable = true) {
            const getMenuNodes = () => _.filter(Array.from(this.$dropdownMenu.find('li')), function(element) {
                return actionable ? angular.element(element).find('a').length > 0 : true;
            });
            let menuNodes = getMenuNodes();

            // Catch edge cases when the dropdown menu had a delayed initialisation
            if (!_.size(menuNodes)) {
                this.initDropdownElements();

                menuNodes = getMenuNodes();
            }

            return menuNodes;
        }

        /**
         * @ngdoc method
         * @name DropdownAccessibilityController#makeMenuNodesNavigable
         * @description Configure the menu items to allow to receive programmatic focus
         * @param {Array} menuNodes An array of Dom nodes representing the menu list items
         * @returns {DropdownAccessibilityController}
         */
        makeMenuNodesNavigable(menuNodes) {
            _.chain(menuNodes)
                .filter((node) => {
                    return !(
                        node.classList.contains('dropdown-header')
                        || node.classList.contains('header')
                        || node.classList.contains('divider')
                    );
                })
                .forEach((node) => (node.setAttribute('tabindex', -1)))
                .value();

            return this;
        }

        /**
         * @ngdoc method
         * @name DropdownAccessibilityController#focusSelectedMenuNode
         * @description Focuses the selected menu item on dropdown open
         * @param {Array} menuNodes An array of Dom nodes representing the menu list items
         * @returns {DropdownAccessibilityController}
         */
        focusSelectedMenuNode(menuNodes) {
            const selectedNode = _.find(menuNodes, (node) => node.classList.contains(this.ACTIVE_MENU_ITEM_CLASS));

            if (selectedNode) {
                this.timeouts.focusSelected = this.$timeout(() => this.focusNode(selectedNode));
            }

            return this;
        }

        /**
         * @ngdoc method
         * @name DropdownAccessibilityController#onDropdownOpen
         * @description Actively listens for the user input
         * @returns {DropdownAccessibilityController}
         */
        onDropdownOpen() {
            if (this.listenersInstalled) {
                this.onDropdownClose();
            }

            this.menuNodes = this.getDropdownMenuNodes();

            this.makeMenuNodesNavigable(this.menuNodes);

            this.$dropdownMenu.attr('aria-expanded', true);

            this.focusSelectedMenuNode(this.menuNodes);

            this.$document.bind('keypress', this.onKeypressCallback);

            this.$dropdownMenu.on('mousemove', this.onMouseMoveCallback);
            this.$dropdownMenu.on('mouseenter', 'li', this.debouncedOnMouseEnterCallback);

            this.isDropdownOpened = true;

            this.listenersInstalled = true;

            return this;
        }

        /**
         * @ngdoc method
         * @name DropdownAccessibilityController#onDropdownClose
         * @description Uninstalls listeners
         * @returns {DropdownAccessibilityController}
         */
        onDropdownClose() {
            if (!this.listenersInstalled) {
                return this;
            }

            this.menuNodes = [];

            this.$dropdownMenu.attr('aria-expanded', false);

            this.$document.unbind('keypress', this.onKeypressCallback);

            this.$dropdownMenu.off('mousemove', this.onMouseMoveCallback);
            this.$dropdownMenu.off('mouseenter', 'li', this.debouncedOnMouseEnterCallback);

            this.$dropdownToggle.focus();

            this.isDropdownOpened = false;

            this.listenersInstalled = false;

            return this;
        }

        /**
         * @ngdoc method
         * @name DropdownAccessibilityController#onKeypress
         * @description Callback executed on user input
         * @param {Event} event The keyboard input event
         */
        onKeypress(event) {
            this.useMouseFocus = false;

            // Bypass accessibility features if user focuses on an input.
            if (_.chain(event).get('target.tagName').upperCase().value() === 'INPUT') {
                return;
            }

            if (!this.debouncedAssertFocus) {
                this.debouncedAssertFocus = _.debounce(this.assertFocusCallback, this.debounceWait);
            }

            this.searchContent += _.toLower(this.getEventPrintableCharacter(event));

            this.debouncedAssertFocus(event);

            // For large dropdowns prevent scrolling on key press as key press is used as printable character to detect list items to focus
            if (this.getEventKeyCode(event) === this.SPACE_KEY_CODE) {
                event.preventDefault();
            }

            if (this.getEventKeyCode(event) === this.ENTER_KEY_CODE) {
                const activeElement = this.$document.get(0).activeElement;
                const focusedMenuNode = _.find(this.menuNodes, (node) => (node === activeElement));

                if (activeElement && focusedMenuNode) {
                    const $focusedMenuNode = angular.element(focusedMenuNode);
                    const $hyperlinkElement = $focusedMenuNode.find('a');

                    // Gives priority to the menu item hyperlink if it exists (by convention the menu item should contain a hyperlink element)
                    // Allows to execute the angularJS handlers placed inside the list item node
                    if (_.size($hyperlinkElement)) {
                        $hyperlinkElement.trigger('click');
                    } else {
                        $focusedMenuNode.trigger('click');
                    }
                }
            }
        }

        /**
         * @ngdoc method
         * @name DropdownAccessibilityController#onMouseMove
         * @description Callback on mouse move within the dropdown
         */
        onMouseMove() {
            this.useMouseFocus = true;
        }

        /**
         * @ngdoc method
         * @name DropdownAccessibilityController#onMouseMove
         * @description Callback on mouse enter a menu item node
         * @param {Event} $event The mousenter event
         */
        onMouseEnter($event) {
            if (this.useMouseFocus) {
                this.focusNode($event.currentTarget);
            }
        }

        /**
         * @ngdoc method
         * @name DropdownAccessibilityController#assertFocus
         * @description Focuses the menu item that qualifies for the input provided by the user (keyboard typing)
         * @param {Event} event The keyboard input event
         */
        assertFocus(event) {
            const eventQualifies = (_.size(this.searchContent) >= this.minLength);
            const elementIndex = _.findIndex(this.menuNodes, (node) => _.startsWith(this.getChildTextContent(node), this.searchContent));

            if (eventQualifies && elementIndex > -1) {
                const focusNode = this.menuNodes[elementIndex];

                this.focusNode(focusNode);

                this.scrollIntoView(angular.element(focusNode));
            }

            this.searchContent = '';

            delete this.debouncedAssertFocus;
        }

        /**
         * @ngdoc method
         * @name DropdownAccessibilityController#focusNode
         * @description Focuses the provided menu list node
         * @param {HTMLElement} focusNode The node to focus
         * @returns {DropdownAccessibilityController}
         */
        focusNode(focusNode) {
            const elementIndex = _.findIndex(this.menuNodes, (node) => node.isEqualNode(focusNode));

            focusNode.focus();

            // Updates the dropdown controller with the focused element so that the keyboard navigation knows where to start on up|down keypress
            if (this.dropdown) {
                this.dropdown.selectedOption = elementIndex;
            }

            return this;
        }

        /**
         * @ngdoc method
         * @name DropdownAccessibilityController#getEventPrintableCharacter
         * @description Retrieves the printable character for a keyboard event. Returns empty string for any non-printable key strokes.
         * @param {Event} event The keyboard input event
         * @returns {string}
         */
        getEventPrintableCharacter(event) {
            const keyCode = this.getEventKeyCode(event);

            if (keyCode) {
                return String.fromCharCode(keyCode);
            }

            return '';
        }

        /**
         * @ngdoc method
         * @name DropdownAccessibilityController#getEventKeyCode
         * @description Retrieves the key code of a keyboard event. Mitigates the cross-browser inconsistencies.
         * @param {Event} event The keyboard input event
         * @returns {number}
         */
        getEventKeyCode(event) {
            return event.which || event.keyCode;
        }

        /**
         * @ngdoc method
         * @name DropdownAccessibilityController#getChildTextContent
         * @description Retrieves the "normalised" textContent of a list menu item.
         * @param {Node} element The dom node of a menu list item
         * @returns {number}
         */
        getChildTextContent(element) {
            return _.chain(element.textContent).trim().toLower().value();
        }
    }

    angular.module('wtUi')
        .directive('dropdownAccessibility', [function() {
            return {
                require: {
                    // Optionally depends on the UI bootstrap dropdown directive {@link https://github.com/angular-ui/bootstrap/blob/master/src/dropdown/dropdown.js}
                    dropdown: '?^dropdown'
                },
                bindToController: true,
                restrict: 'A',
                controller: DropdownAccessibilityController
            };
        }]);
})(window.angular, window._);
